import { useCallback, useEffect, useMemo } from 'react';

import type { ProposalDecisions, ProposalSuggestion, UUID } from '@aline/common';
import { ProposalDecisionsSchema, ProposalSuggestionsSchema } from '@aline/common';
import { Apollo } from 'src/gql/generated';

import { filterToRunCategories, getIsAnyRuleRunning } from './playbook-run-state';
import type { PlaybookProposal } from './playbook-run-state';

/**
 * How long a run may go without a live update before the panel asks the server
 * directly. A rule usually finishes every few seconds, so a healthy run never
 * waits this long and never refetches.
 */
export const RUN_QUIET_REFETCH_MS = 15_000;

/**
 * One request per refetch, never a share of one already in flight. The refetch
 * exists for when something upstream has stalled, and a stalled request that
 * every later refetch joined would stall the recovery with it.
 */
export const RUN_QUERY_CONTEXT = { queryDeduplication: false };

/**
 * One playbook's run against the open document, down to the categories and
 * rules a run covers. Live updates land in the normalized cache from
 * OnDocPlaybooksUpdated, registered once on the drawer.
 */
export function usePlaybookRun({ documentId, playbookId }: { documentId: UUID; playbookId: UUID }) {
  const { data, loading, refetch } = Apollo.useGetDocPlaybookQuery({
    context: RUN_QUERY_CONTEXT,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { documentId, playbookId },
  });
  const loaded = data?.playbook ?? null;
  const playbook = useMemo(
    () => (loaded ? { ...loaded, categories: filterToRunCategories(loaded.categories) } : null),
    [loaded]
  );

  useRefetchWhileRunIsQuiet({
    isRunning: getIsAnyRuleRunning(playbook?.categories ?? []),
    refetch,
    runData: playbook,
  });

  return { loading, playbook };
}

/**
 * The safety net under the live updates. Each update announces one rule, so an
 * update that never arrives is never made up for by the next one: that rule
 * would read Running for good. Updates do get lost. The socket drops, the
 * server restarts mid-run for a deploy, or the server stalls building one.
 *
 * So while the run still has a rule running, the panel refetches whenever
 * `RUN_QUIET_REFETCH_MS` passes with no change to the run's data, and keeps
 * doing so until a change arrives or nothing is running.
 *
 * Every change to `runData` restarts the wait, which is what keeps a healthy
 * run from refetching. Re-running the setup only starts a timer, so a pane
 * that is hidden and shown again refetches nothing by being shown.
 */
export function useRefetchWhileRunIsQuiet({
  isRunning,
  refetch,
  runData,
}: {
  isRunning: boolean;
  refetch: () => Promise<unknown>;
  /** The run as last received. A new identity means an update just landed. */
  runData: unknown;
}): void {
  useEffect(() => {
    if (!isRunning) return;

    const timer = window.setInterval(() => {
      // A failed refetch is tried again on the next tick.
      void refetch().catch(() => undefined);
    }, RUN_QUIET_REFETCH_MS);

    return () => window.clearInterval(timer);
  }, [isRunning, refetch, runData]);
}

/** The client-side verbs for one review decision on a suggestion. */
export type SuggestionReviewVerb = 'accept' | 'reject' | 'unreject';

const DECISION_BY_VERB: Record<SuggestionReviewVerb, Apollo.PlaybookSuggestionReviewDecision> = {
  accept: Apollo.PlaybookSuggestionReviewDecision.Accepted,
  reject: Apollo.PlaybookSuggestionReviewDecision.Rejected,
  unreject: Apollo.PlaybookSuggestionReviewDecision.Undecided,
};

export function usePlaybookRunActions({
  documentId,
  playbookId,
}: {
  documentId: UUID;
  playbookId: UUID;
}) {
  const [startMutation, { loading: starting }] = Apollo.useStartPlaybookRunMutation();
  const [cancelMutation] = Apollo.useCancelPlaybookRuleJobsMutation();
  const [rerunRuleMutation] = Apollo.useStartPlaybookRuleJobsMutation();
  const [resolveMutation] = Apollo.useResolvePlaybookRuleJobMutation();
  const [clearResolutionMutation] = Apollo.useClearPlaybookRuleJobResolutionMutation();
  const [reviewSuggestionMutation] = Apollo.useReviewPlaybookSuggestionMutation();

  const runPlaybook = useCallback(
    async () => startMutation({ variables: { documentId, playbookId } }),
    [documentId, playbookId, startMutation]
  );
  const cancelRun = useCallback(
    async () => cancelMutation({ variables: { documentId, playbookId } }),
    [cancelMutation, documentId, playbookId]
  );
  const rerunRules = useCallback(
    async (ruleIds: UUID[]) => {
      if (!ruleIds.length) return;
      return rerunRuleMutation({ variables: { documentId, ruleIds } });
    },
    [documentId, rerunRuleMutation]
  );
  const rerunRule = useCallback(async (ruleId: UUID) => rerunRules([ruleId]), [rerunRules]);
  const resolveRule = useCallback(
    async (input: { jobId: UUID; kind: Apollo.PlaybookRuleResolutionKind; positionId?: UUID }) =>
      resolveMutation({
        variables: {
          input: {
            id: input.jobId,
            kind: input.kind,
            positionId: input.positionId ?? null,
          },
        },
      }),
    [resolveMutation]
  );
  const clearResolution = useCallback(
    async (jobId: UUID) => clearResolutionMutation({ variables: { input: { id: jobId } } }),
    [clearResolutionMutation]
  );
  /**
   * Records one review decision. For accepts the caller materializes first —
   * the mutation only records what is already in the document. Returns the
   * job so callers can see whether this decision completed the review (the
   * server auto-resolves on the last one).
   */
  const reviewSuggestion = useCallback(
    async (input: { jobId: UUID; suggestionId: string; verb: SuggestionReviewVerb }) =>
      reviewSuggestionMutation({
        variables: {
          input: {
            decision: DECISION_BY_VERB[input.verb],
            id: input.jobId,
            suggestionId: input.suggestionId,
          },
        },
      }),
    [reviewSuggestionMutation]
  );

  return {
    cancelRun,
    clearResolution,
    rerunRule,
    rerunRules,
    resolveRule,
    reviewSuggestion,
    runPlaybook,
    starting,
  };
}

export interface ParsedProposal {
  decisions: ProposalDecisions;
  suggestions: ProposalSuggestion[];
}

/**
 * A proposal's payloads travel as opaque JSON and are parsed here with the
 * same zod schemas the server validated them with — the parse is a corruption
 * guard, not a translation. Null means the stored payload does not match the
 * schema, which the caller should treat as an unusable proposal.
 */
export function parseProposal(proposal: PlaybookProposal): ParsedProposal | null {
  const suggestions = ProposalSuggestionsSchema.safeParse(proposal.suggestions);
  const decisions = ProposalDecisionsSchema.safeParse(proposal.decisions);
  if (!suggestions.success || !decisions.success) return null;

  return { decisions: decisions.data, suggestions: suggestions.data };
}
